import { useRef, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { SchoolSearch } from '../SchoolSearch';
import { toTitleCase } from '../../utils/string';
import { Mixpanel } from '../../services/Mixpanel';
import {
  ROLES,
  SUBJECTS,
  CLASS_OPTIONS,
  INTERVENTION_SPECIALIST_TYPES,
  OTHER,
} from '../../constants/userProfile';

// Profile question UI shared by the onboarding wizard (OnboardingWizardBTS2026)
// and the profile editor (EditProfileModal), so the two stay in sync.
// `trackingPrefix` keeps the two surfaces' Mixpanel events distinguishable.

export const GRADE_LEVELS = [
  {
    title: 'K',
    server_value: 'K',
  },
  {
    title: '1',
    server_value: '1',
  },
  {
    title: '2',
    server_value: '2',
  },
  {
    title: '3',
    server_value: '3',
  },
  {
    title: '4',
    server_value: '4',
  },
  {
    title: '5',
    server_value: '5',
  },
  {
    title: '6',
    server_value: '6',
  },
  {
    title: '7',
    server_value: '7',
  },
  {
    title: '8',
    server_value: '8',
  },
  {
    title: '9',
    server_value: '9',
  },
  {
    title: '10',
    server_value: '10',
  },
  {
    title: '11',
    server_value: '11',
  },
  {
    title: '12',
    server_value: '12',
  },
  {
    title: 'Other',
    server_value: 'Other',
  },
];

export const doPreferToKnowSchool = (roles) => {
  const isDistrictLeader = roles.includes('district_leader');
  const isOtherRole = roles.includes('other');
  return !isDistrictLeader && !isOtherRole;
};

export const isInterventionSpecialist = (roles) => {
  // if any of the roles begin with 'intervention_specialist'
  return roles?.some((r) => r?.startsWith?.('intervention_specialist'));
};

export const getInitialSelections = (diffitUser) => {
  const gradeLevels = diffitUser.grade_levels;

  const existingGradeLevelArray = gradeLevels.map((grade) => grade.grade_level);
  const userGradeLevels = GRADE_LEVELS.filter((g) =>
    existingGradeLevelArray.includes(g.server_value)
  ).map((g) => g.server_value);

  const subjects = diffitUser.subjects.map((s) => s.subject) || [];

  const roles = diffitUser.roles?.map((r) => r.role) || [];
  const classes = diffitUser.classes?.map((c) => c.class_name) || [];

  return {
    roles,
    grade_levels: userGradeLevels,
    subjects: subjects,
    classes: classes,
    school_records: diffitUser.school_records || [], // fixme
    does_not_work_for_school_or_district: !!diffitUser.does_not_work_for_school_or_district,
  };
};

// Subjects implied by the selected classes, for users who pick classes
// rather than subjects directly.
export const getInferredSubjectsFromClasses = (classes) => {
  return (classes || []).reduce((acc, c) => {
    const classObj = CLASS_OPTIONS.find((co) => co.server_value === c);
    const subjects = classObj?.subjects || [OTHER];
    return [...acc, ...subjects];
  }, []);
};

export const RoleSelection = ({
  selections,
  setSelections,
  initialFocusRef,
  trackingPrefix = 'Onboarding Wizard',
  // the wizard scrolls this list itself; the edit form scrolls the whole panel
  scrollable = true,
}) => {
  // if any of the selections are from the "More" section, show the "More" section
  const showMore = selections.roles.some((r) => {
    const indexOfRole = ROLES.findIndex((role) => role.server_value === r);
    return indexOfRole >= 3;
  });
  const [moreShown, setMoreShown] = useState(showMore);

  const isSelected = (role) => {
    return (
      selections.roles.includes(role.server_value) ||
      (isInterventionSpecialist(selections.roles) &&
        role.server_value === 'intervention_specialist')
    );
  };

  const onSelectRole = (role) => {
    const wasSelected = isSelected(role);
    const didTapInterventionSpecialist = role.server_value === 'intervention_specialist';

    let newRoles = wasSelected
      ? selections.roles.filter((r) => r !== role.server_value)
      : [...selections.roles, role.server_value];

    if (didTapInterventionSpecialist && wasSelected) {
      newRoles = newRoles.filter((r) => !r.startsWith('intervention_specialist'));
    }

    Mixpanel.track(`${trackingPrefix} - Role Changed`, {
      role_selected: role.server_value,
      from: wasSelected,
      to: !wasSelected,
      new_roles: newRoles,
    });
    setSelections({
      ...selections,
      roles: newRoles,
    });
  };

  return (
    <div
      className={`flex flex-col gap-y-8 px-3 -mx-3 py-2 -my-2 ${scrollable ? 'overflow-y-auto' : ''}`}
      style={scrollable ? { maxHeight: 'calc(100vh - 400px)' } : undefined}>
      <div className='flex flex-col gap-y-2'>
        {ROLES.slice(0, moreShown ? undefined : 3).map((role) => {
          return (
            <button
              key={role.server_value}
              ref={role.server_value === 'classroom_teacher' ? initialFocusRef : null}
              onClick={() => onSelectRole(role)}
              className={`w-full px-3 py-2 border rounded-md ${isSelected(role) ? 'bg-emerald-100 border-emerald-500 border hover:bg-emerald-200' : 'border-gray-500 hover:bg-gray-100 '}`}>
              <div>
                {role.title}
                {role.subtitle && <span className='text-gray-500 text-sm'> {role.subtitle}</span>}
              </div>
            </button>
          );
        })}
        {!moreShown && (
          <>
            <div className='w-full h-px bg-gray-200 my-4' />
            <button
              onClick={() => {
                Mixpanel.track(`${trackingPrefix} - View More Roles Clicked`);
                setMoreShown(!moreShown);
              }}
              className='hover:text-emerald-600'>
              {moreShown ? 'View Less' : 'View More'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const InterventionSpecialistTypeSelection = ({
  selections,
  setSelections,
  trackingPrefix = 'Onboarding Wizard',
  // the wizard keeps the broad role when no subtype is selected, so the Back
  // button can return to this step; the edit form drops the role instead
  keepBroadRoleWhenEmpty = true,
}) => {
  const onSelectInterventionSpecialistType = (type) => {
    const wasSelected = selections.roles.includes(type.server_value);
    let newRoles = wasSelected
      ? selections.roles.filter((r) => r !== type.server_value)
      : [...selections.roles, type.server_value];

    if (newRoles.some((r) => r.startsWith('intervention_specialist_'))) {
      // a subtype implies the broad "intervention_specialist" role
      newRoles = newRoles.filter((r) => r !== 'intervention_specialist');
    } else if (keepBroadRoleWhenEmpty) {
      newRoles = [...newRoles, 'intervention_specialist'];
    }

    Mixpanel.track(`${trackingPrefix} - Intervention Specialist Type Changed`, {
      intervention_specialist_type_selected: type.server_value,
      from: wasSelected,
      to: !wasSelected,
      new_roles: newRoles,
    });
    setSelections({
      ...selections,
      roles: newRoles,
    });
  };

  return (
    <div className='flex flex-col gap-y-2'>
      {INTERVENTION_SPECIALIST_TYPES.map((type) => {
        const isSelected = selections.roles.includes(type.server_value);
        return (
          <button
            key={type.server_value}
            onClick={() => onSelectInterventionSpecialistType(type)}
            className={`w-full px-3 py-2 border rounded-md ${isSelected ? 'bg-emerald-100 border-emerald-500 border hover:bg-emerald-200' : 'border-gray-500 hover:bg-gray-100 '}`}>
            <div>{type.title}</div>
          </button>
        );
      })}
    </div>
  );
};

export const GradeSelection = ({
  selections,
  setSelections,
  trackingPrefix = 'Onboarding Wizard',
}) => {
  const onSelectGrade = (grade) => {
    const wasSelected = selections.grade_levels.includes(grade.server_value);
    const newGradeLevels = wasSelected
      ? selections.grade_levels.filter((g) => g !== grade.server_value)
      : [...selections.grade_levels, grade.server_value];

    Mixpanel.track(`${trackingPrefix} - Grade Changed`, {
      grade_selected: grade.server_value,
      from: wasSelected,
      to: !wasSelected,
      new_grade_levels: newGradeLevels,
    });
    setSelections({
      ...selections,
      grade_levels: newGradeLevels,
    });
  };

  const isSelected = (grade) => {
    return selections.grade_levels.includes(grade.server_value);
  };

  return (
    <div className='flex flex-col gap-y-4'>
      {/* grid of grade levels
        4 options in 1st row, 5 options in 2nd row, 4 options in 3rd row, "Other" in 4th row */}
      <div className='flex gap-x-2'>
        {GRADE_LEVELS.slice(0, 4).map((grade) => (
          <GradeSelectionButton
            isSelected={isSelected(grade)}
            key={grade.server_value}
            grade={grade}
            onSelect={onSelectGrade}
          />
        ))}
      </div>
      <div className='flex gap-x-2'>
        {GRADE_LEVELS.slice(4, 9).map((grade) => (
          <GradeSelectionButton
            isSelected={isSelected(grade)}
            key={grade.server_value}
            grade={grade}
            onSelect={onSelectGrade}
          />
        ))}
      </div>
      <div className='flex gap-x-2'>
        {GRADE_LEVELS.slice(9, 13).map((grade) => (
          <GradeSelectionButton
            isSelected={isSelected(grade)}
            key={grade.server_value}
            grade={grade}
            onSelect={onSelectGrade}
          />
        ))}
      </div>
      <div className='flex gap-x-2'>
        <GradeSelectionButton
          isSelected={isSelected(GRADE_LEVELS[13])}
          key={GRADE_LEVELS[13].server_value}
          grade={GRADE_LEVELS[13]}
          width='w-[112px]'
          onSelect={onSelectGrade}
        />
      </div>
    </div>
  );
};

const GradeSelectionButton = ({ grade, width = 'w-[52px]', onSelect, isSelected }) => {
  return (
    <button
      onClick={() => onSelect(grade)}
      key={grade.server_value}
      className={`${width} px-3 py-2 border rounded-md ${isSelected ? 'bg-emerald-100 border-emerald-500 border hover:bg-emerald-200' : 'border-gray-500 hover:bg-gray-100'}`}>
      {grade.title}
    </button>
  );
};

export const SubjectSelection = ({
  selections,
  setSelections,
  trackingPrefix = 'Onboarding Wizard',
  scrollable = true,
}) => {
  const showMore = selections.subjects.some((s) => {
    const indexOfSubject = SUBJECTS.findIndex((subject) => subject.server_value === s);
    return indexOfSubject >= 5;
  });
  const [moreShown, setMoreShown] = useState(showMore);
  const onSelectSubject = (subject) => {
    const wasSelected = selections.subjects.includes(subject.server_value);
    const newSubjects = wasSelected
      ? selections.subjects.filter((s) => s !== subject.server_value)
      : [...selections.subjects, subject.server_value];

    Mixpanel.track(`${trackingPrefix} - Subjects Changed`, {
      subject_selected: subject.server_value,
      from: wasSelected,
      to: !wasSelected,
      new_subjects: newSubjects,
    });
    setSelections({
      ...selections,
      subjects: newSubjects,
    });
  };
  return (
    <div className='flex flex-col gap-y-8'>
      {/* SUBJECTS SCROLLABLE CONTAINER */}
      <div
        className={`flex flex-col gap-y-2 px-3 -mx-3 py-2 -my-2 ${scrollable ? 'overflow-y-auto' : ''}`}
        style={scrollable ? { maxHeight: 'calc(100vh - 300px)' } : undefined}>
        {SUBJECTS.slice(0, moreShown ? undefined : 5).map((subject) => {
          const isSelected = selections.subjects.includes(subject.server_value);
          return (
            <button
              key={subject.server_value}
              onClick={() => onSelectSubject(subject)}
              className={`w-full px-3 py-2 border rounded-md ${isSelected ? 'bg-emerald-100 border-emerald-500 border hover:bg-emerald-200' : 'border-gray-500 hover:bg-gray-100 '}`}>
              <div>
                {subject.title}
                {subject.subtitle && (
                  <span className='text-gray-500 text-sm'> {subject.subtitle}</span>
                )}
              </div>
            </button>
          );
        })}
        {!moreShown && (
          <>
            <div className='w-full h-px bg-gray-200 my-4' />
            <button
              onClick={() => {
                Mixpanel.track(`${trackingPrefix} - View More Subjects Clicked`);
                setMoreShown(!moreShown);
              }}
              className='hover:text-emerald-600'>
              {moreShown ? 'View Less Subjects' : 'View More Subjects'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const ClassesSelection = ({
  selections,
  setSelections,
  trackingPrefix = 'Onboarding Wizard',
}) => {
  // Convert server values to full class objects
  const selectedClassObjects = (selections.classes || []).map((serverValue) => {
    const classOption = CLASS_OPTIONS.find((c) => c.server_value === serverValue);
    return classOption || { name: serverValue, server_value: serverValue };
  });

  return (
    <div className='flex flex-col gap-y-8'>
      <SelectClassesCombobox
        key={selections.classes.join(',')}
        selectedClasses={selectedClassObjects}
        trackingPrefix={trackingPrefix}
        onClassesChange={(classes) => {
          setSelections({
            ...selections,
            classes,
          });
        }}
      />
    </div>
  );
};

export const SchoolSelection = ({
  selections,
  setSelections,
  isAddSchoolFormShown,
  setIsAddSchoolFormShown,
  trackingPrefix = 'Onboarding Wizard',
  mixpanelLocation = 'onboarding_wizard',
}) => {
  const onSelectSchool = (school) => {
    setSelections({
      ...selections,
      school_records: school ? [school] : [],
    });
  };

  const onToggleIDontWorkForSchool = () => {
    const newVal = !selections.does_not_work_for_school_or_district;
    Mixpanel.track(`${trackingPrefix} - I don't work for a school or district toggled`, {
      from: !newVal,
      to: newVal,
    });
    setSelections({
      ...selections,
      school_records: newVal ? [] : selections.school_records,
      does_not_work_for_school_or_district: newVal,
    });
  };

  const onAddSchoolFormShown = () => {
    setIsAddSchoolFormShown(true);
  };
  const onAddSchoolFormHidden = () => {
    setIsAddSchoolFormShown(false);
  };

  if (selections.school_records.length > 1) {
    // unusual case - the user has multiple schools.
    // this can only happen if they have been added to multiple orgs by an admin.
    return (
      <MultipleSchoolsContent
        selections={selections}
        setSelections={setSelections}
        onAddSchoolFormShown={onAddSchoolFormShown}
        onAddSchoolFormHidden={onAddSchoolFormHidden}
        trackingPrefix={trackingPrefix}
        mixpanelLocation={mixpanelLocation}
      />
    );
  }

  // the usual case - the user has a single school
  const school = selections.school_records[0];

  return (
    <div className='flex flex-col gap-y-4 text-gray-600'>
      <SchoolSearch
        key={selections.does_not_work_for_school_or_district}
        disabled={selections.does_not_work_for_school_or_district}
        selectedSchool={school}
        onSelectSchool={onSelectSchool}
        onAddSchoolFormShown={onAddSchoolFormShown}
        onAddSchoolFormHidden={onAddSchoolFormHidden}
        mixpanelLocation={mixpanelLocation}
        placeholder={
          doPreferToKnowSchool(selections.roles) ? 'School Name' : 'School or District Name'
        }
        labelClassName={isAddSchoolFormShown ? 'text-sm font-semibold' : 'sr-only'}
      />
      {!isAddSchoolFormShown && (
        <div
          className={`${school ? '' : ''} flex items-center gap-x-2 bg-gray-100 px-2 py-3 rounded-md cursor-pointer`}
          onClick={onToggleIDontWorkForSchool}>
          <input
            type='checkbox'
            onChange={onToggleIDontWorkForSchool}
            className='h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600 cursor-pointer'
            checked={selections.does_not_work_for_school_or_district}
          />
          <label className='text-sm font-semibold pointer-events-none select-none'>
            I don't work for a school or district
          </label>
        </div>
      )}
    </div>
  );
};

const MultipleSchoolsContent = ({
  selections,
  setSelections,
  onAddSchoolFormShown,
  onAddSchoolFormHidden,
  trackingPrefix,
  mixpanelLocation,
}) => {
  const [didClickToAddSchool, setDidClickToAddSchool] = useState(false);
  return (
    <div
      className={`flex flex-col gap-y-4 px-3 -mx-3 pb-2 -mb-2 ${didClickToAddSchool ? '' : 'overflow-y-auto h-[300px]'}`}>
      {!didClickToAddSchool &&
        selections.school_records.map((school) => (
          <div key={school.id} className='flex justify-between gap-x-4'>
            <div className='flex flex-col gap-y-0.5 '>
              <div className='text-base font-semibold line-clamp-1'>{school.name}</div>
              <div className='text-sm text-gray-500 line-clamp-1'>
                {school.state && `${toTitleCase(school.city)}, ${toTitleCase(school.state)}`}
              </div>
              <div className='text-sm text-gray-500 line-clamp-1'>
                {!school.state && school.country && toTitleCase(school.country)}
              </div>
            </div>
            <div>
              <button
                onClick={() => {
                  Mixpanel.track(`${trackingPrefix} - Remove School Clicked`, {
                    school_name: school.name,
                  });
                  setSelections({
                    ...selections,
                    school_records: selections.school_records.filter((s) => s.id !== school.id),
                  });
                }}
                className='text-sm text-red-500 hover:text-red-700'>
                Remove
              </button>
            </div>
          </div>
        ))}
      {!didClickToAddSchool && (
        <button
          className='text-sm hover:text-gray-500 rounded-md px-3 py-2'
          onClick={() => {
            Mixpanel.track(`${trackingPrefix} - Add Another School Clicked`);
            setDidClickToAddSchool(true);
            onAddSchoolFormShown();
          }}>
          Add another school
        </button>
      )}
      <div>
        {didClickToAddSchool && (
          <div className='px-3 -mx-3 py-0.5 flex flex-col gap-y-4'>
            {/* SCHOOL FORM WRAPPER */}
            <SchoolSearch
              disabled={selections.does_not_work_for_school_or_district}
              selectedSchool={null}
              onSelectSchool={(school) => {
                setSelections({
                  ...selections,
                  school_records: [...selections.school_records, school],
                });
                setDidClickToAddSchool(false);
                onAddSchoolFormHidden();
              }}
              onAddSchoolFormShown={onAddSchoolFormShown}
              onAddSchoolFormHidden={() => {
                Mixpanel.track(`${trackingPrefix} - Cancel Add Another School Clicked`);
                setDidClickToAddSchool(false);
                onAddSchoolFormHidden();
              }}
              labelClassName={didClickToAddSchool ? 'text-sm font-semibold' : 'sr-only'}
              showCancelButton={true}
              mixpanelLocation={`${mixpanelLocation}_multiple_schools`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const normalizeString = (str) => {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const SelectClassesCombobox = ({ selectedClasses, onClassesChange, trackingPrefix }) => {
  const MAX_CLASSES = 10;
  const defaultClasses = CLASS_OPTIONS;
  const [query, setQuery] = useState('');

  // Filter out already selected classes and filter by query
  const filteredClasses = defaultClasses
    .filter((c) => !selectedClasses.some((selected) => selected.server_value === c.server_value))
    .filter((c) => normalizeString(c.name).includes(normalizeString(query)));

  const showAddCustomClassOption =
    query.trim() &&
    !CLASS_OPTIONS.some((c) => normalizeString(c.name) === normalizeString(query.trim())) &&
    selectedClasses.length < MAX_CLASSES;

  const scrollContainerRef = useRef(null);
  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  };
  const getInputClassName = (isDisabled) => {
    const baseClasses =
      'w-full border rounded-md px-3 py-2 border-gray-400 focus:border-emerald-500 focus:ring-emerald-500 placeholder:text-gray-400 placeholder:text-sm';
    const disabledClasses = 'border-gray-200 cursor-not-allowed';

    return isDisabled ? `${baseClasses} ${disabledClasses}` : baseClasses;
  };

  return (
    <div>
      <Combobox
        multiple
        value={selectedClasses}
        onChange={(classes) => {
          // Prevent adding more than MAX_CLASSES
          if (classes.length > MAX_CLASSES) {
            return;
          }

          const newClass = classes.filter((c) => !selectedClasses.includes(c))?.[0];
          const newClasses = classes.map((c) => c.server_value);
          Mixpanel.track(`${trackingPrefix} - Classes Changed`, {
            class_selected: newClass?.server_value,
            from: false,
            to: true,
            new_classes: newClasses,
          });
          onClassesChange(newClasses);
          setQuery('');
          scrollToBottom();
        }}
        by='server_value'>
        {selectedClasses.length > 0 && (
          <ul
            ref={scrollContainerRef}
            className='flex flex-wrap gap-2 mb-4 max-h-36 overflow-y-auto'>
            {selectedClasses.map((c) => (
              <li
                key={c.server_value}
                className='bg-emerald-100 text-sm text-emerald-800 px-2 py-1 rounded-md flex items-center gap-x-1 min-w-0'>
                <span className='truncate'>{c.name}</span>
                <button
                  onClick={() => {
                    Mixpanel.track(`${trackingPrefix} - Classes Changed`, {
                      class_selected: c.server_value,
                      from: true,
                      to: false,
                      new_classes: selectedClasses
                        .map((s) => s.server_value)
                        .filter((value) => value !== c.server_value),
                    });
                    onClassesChange(
                      selectedClasses
                        .map((s) => s.server_value)
                        .filter((value) => value !== c.server_value)
                    );
                  }}
                  className='text-emerald-600 hover:text-emerald-800 hover:bg-emerald-200 rounded-full px-1.5 flex items-center justify-center'>
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className='relative'>
          <Combobox.Button className='w-full'>
            <Combobox.Input
              aria-label='Classes'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={
                selectedClasses.length >= MAX_CLASSES
                  ? 'Maximum classes reached'
                  : 'Search for Subjects and Classes'
              }
              className={getInputClassName(selectedClasses.length >= MAX_CLASSES)}
              disabled={selectedClasses.length >= MAX_CLASSES}
            />
          </Combobox.Button>
          <Combobox.Options
            className={`absolute z-10 w-full mt-1 bg-white rounded-md ${filteredClasses.length > 0 || showAddCustomClassOption ? 'shadow-lg border' : ''} max-h-60 overflow-auto`}>
            {filteredClasses.map((c) => (
              <Combobox.Option
                key={c.server_value}
                value={c}
                className={({ active }) =>
                  `px-3 py-2 cursor-pointer text-sm ${active ? 'bg-emerald-100 text-emerald-900' : 'text-gray-900'}`
                }>
                {c.name}
              </Combobox.Option>
            ))}
            {showAddCustomClassOption && (
              <Combobox.Option
                value={{ server_value: query.trim(), name: query.trim(), subjects: [OTHER] }}
                className={({ active }) =>
                  `px-3 py-2 cursor-pointer text-sm ${active ? 'bg-emerald-100 text-emerald-900' : 'text-gray-900'}`
                }>
                Add "{query.trim()}"
              </Combobox.Option>
            )}
          </Combobox.Options>
        </div>
      </Combobox>
      {selectedClasses.length >= MAX_CLASSES && (
        <div className='text-xs text-gray-500 mt-1'>
          You've reached the maximum of {MAX_CLASSES} classes. Please remove a class to add another.
        </div>
      )}
    </div>
  );
};
